import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { edgeErrorMessage } from "@/lib/edge-errors";

/** Største bilde «Last opp bilde» godtar (samme grense som edge function customer-logo). */
export const LOGO_MAX_BYTES = 512 * 1024;
export const LOGO_ACCEPT = "image/png,image/jpeg,image/svg+xml,image/webp";

export type CustomerLogoChange =
  | { action: "website"; website: string }
  | { action: "upload"; file: File }
  | { action: "remove" };

interface LogoResponse {
  logo_url: string | null;
  source: string;
  error?: string;
}

/** Filen som base64 uten data:-prefiks. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(new Error("Kunne ikke lese bildet"));
    reader.readAsDataURL(file);
  });
}

/**
 * «Endre logo» på kundekortet (VIB-2935): hent fra nettside, last opp eller
 * fjern, via edge function customer-logo. Alle ansatte kan.
 */
export function useChangeCustomerLogo(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (change: CustomerLogoChange): Promise<LogoResponse> => {
      let body: Record<string, unknown>;
      if (change.action === "upload") {
        if (change.file.size > LOGO_MAX_BYTES) throw new Error("Bildet er større enn 512 kB");
        body = { customer_id: customerId, action: "upload", content_type: change.file.type, image_base64: await fileToBase64(change.file) };
      } else if (change.action === "website") {
        body = { customer_id: customerId, action: "website", website: change.website };
      } else {
        body = { customer_id: customerId, action: "remove" };
      }
      const { data, error } = await supabase.functions.invoke<LogoResponse>("customer-logo", { body });
      if (error) throw new Error(await edgeErrorMessage(error, data, "Kunne ikke endre logoen"));
      if (data?.error) throw new Error(data.error);
      return data as LogoResponse;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "customers", { customerId });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
